import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { BookOpenText, Download, AlertCircle, ChevronDown, MoveLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "src/components/reusable/loading";
import ReactTable from "src/components/reusable/ReactTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import axios from "axios";
import jsCookie from "js-cookie";
import { API_PROD_URL } from "src/utils/axios";
import { toast } from "react-hot-toast";
import EmailLogs from "./EmailLogs";
import { LoadingIcon } from "src/components/icons";
import ContactsWidget from "./ContactsWidget";

interface Suggestion {
  title: string;
  reason: string;
  action: string;
  level: "high" | "medium" | "low";
}

// Helper to map severity to a numeric value for sorting
const severityRank = (level: string) => {
  switch (level) {
    case "high": return 3;
    case "medium": return 2;
    case "low": return 1;
    default: return 0;
  }
};

const CampaignDashboard = () => {
  const [activeTab, setActiveTab] = useState("Budget Donut");
  const userId = jsCookie.get("user_id")
  // const userId = "414";
   const [loading, setLoading] = useState(false);
   const [campaignData , setCampaignData] =  useState<any>(null)
   const [dashboardData , setDashboardData] = useState<any>(null);
   const { id } = useParams(); 
   const navigate = useNavigate();
   const [suggestionLoading , setSuggestionLoading] = useState(false)
   const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const COLORS = ["#C3508B", "#F57DBA", "#5E4DAB", "#00C49F", "#FFBB28"];

  const chartData = campaignData?.campaign?.template?.budgeting?.budget_split
  ? Object.entries(campaignData.campaign.template.budgeting.budget_split).map(
      ([key, value], index) => ({
        name: key,
        value: parseFloat((value as string).replace(/[$,]/g, "")), // remove $ and commas
        color: COLORS[index % COLORS.length],
      })
    ): [];

  useEffect(()=>{
    const fetchCampaign = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_PROD_URL}/marketing-agent/campaign`, {
          params: { 
            campaign_id:id,
           },
        })
        setCampaignData(response.data)
   
      } catch (err) {
        console.log("Failed to load campaign data");
      } finally {
        setLoading(false);
      }
    };

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_PROD_URL}/marketing-agent/campaign/analytics`, {
          params: { 
            userId:userId,
            campaign_id:id,
            channel_name:"hubspot"
           },
        })
        setDashboardData(response.data)
        // setCampaignData(response.data)
    
      } catch (err) {
        console.log("Failed to load campaign data");
      } finally {
        setLoading(false);
      }
    };
    const fetchSuggestions = async () => {
      setSuggestionLoading(true);
      try {
        const response = await axios.get(`${API_PROD_URL}/marketing-agent/campaign/${id}/suggestions`, {
          params: { userId: jsCookie.get("user_id") },
        });

        // Sort by severity descending and take top 3
        const sorted = (response.data.suggestions || [])
          .sort((a: Suggestion, b: Suggestion) => severityRank(b.level) - severityRank(a.level))
          .slice(0, 3);

        setSuggestions(sorted);
      } catch (err) {
        console.error("Failed to fetch suggestions", err);
      } finally {
        setSuggestionLoading(false);
      }
    };

    fetchCampaign();
    fetchDashboardData();
     fetchSuggestions();
  },[])

  function safeJsonParse(value:any, fallback = []) {
    try {
      if (!value || value === "undefined") return fallback;
      return JSON.parse(value);
    } catch (e) {
      console.error("JSON parse error:", e);
      return fallback;
    }
  }

  const alertsArray = safeJsonParse(dashboardData?.analytics?.alerts);

  // Table column helpers
  const columnHelper = createColumnHelper<any>();

  // Alerts Panel Table Columns
  const alertsPanelColumns: ColumnDef<any>[] = [
    {
      header: "KPI",
      accessorKey: "kpi",
      cell: (item) => (
        <p className="line-clamp-1 font-medium">{item.row.original.kpi}</p>
      ),
    },
    {
      header: "Value",
      accessorKey: "value",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.value}</p>
      ),
    },
    {
      header: "Action",
      accessorKey: "action",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.action}</p>
      ),
    },
    {
      header: "Duration",
      accessorKey: "duration",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.duration}</p>
      ),
    },
    {
      header: "Condition",
      accessorKey: "condition",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.condition}</p>
      ),
    },
  ];

  // KPI Table Columns
  const kpiTableColumns: ColumnDef<any>[] = [
    {
      header: "Channel",
      accessorKey: "channel",
      cell: (item) => (
        <p className="line-clamp-1 font-medium first-letter:uppercase">{item.row.original.channel}</p>
      ),
    },
    {
      header: "Impression",
      accessorKey: "impressions",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.impressions}</p>
      ),
    },
    {
      header: "Clicks",
      accessorKey: "clicks",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.clicks}</p>
      ),
    },
    {
      header: "CTR",
      accessorKey: "ctr",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.ctr}</p>
      ),
    },
    {
      header: "Spend",
      accessorKey: "spend",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.spend}</p>
      ),
    },
    {
      header: "Sessions",
      accessorKey: "sessions",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.sessions}</p>
      ),
    },
    // {
    //   header: "First Touch",
    //   accessorKey: "newContactsFirstTouch",
    //   cell: (item) => (
    //     <p className="line-clamp-1">{item.row.original.newContactsFirstTouch}</p>
    //   ),
    // },
    // {
    //   header: "Last Touch",
    //   accessorKey: "newContactsLastTouch",
    //   cell: (item) => (
    //     <p className="line-clamp-1">{item.row.original.newContactsLastTouch}</p>
    //   ),
    // },
    {
      header: "Influenced Contacts",
      accessorKey: "influencedContacts",
      cell: (item) => (
        <p className="line-clamp-1">{item.row.original.influencedContacts}</p>
      ),
    },
  ];

  // Pipeline Table Columns
  const pipelineColumns: ColumnDef<any>[] = [
    {
      header: "Deal ID",
      accessorKey: "dealId",
      cell: (item) => (
        <p className="line-clamp-1 font-medium">{item.row.original.dealId}</p>
      ),
    },
    {
      header: "Amount",
      accessorKey: "amount",
      cell: (item) => (
        <p className="line-clamp-1">${item.row.original.amount}</p>
      ),
    },
    {
      header: "Stage",
      accessorKey: "stage",
      cell: (item) => (
        <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs font-medium">
          {item.row.original.stage}
        </span>
      ),
    },
    {
      header: "Close Date",
      accessorKey: "close_date",
      cell: (item) => (
        <p className="line-clamp-1">
          {item.row.original.close_date 
            ? new Date(item.row.original.close_date).toLocaleDateString()
            : "N/A"}
        </p>
      ),
    },
    {
      header: "Contact Email",
      accessorKey: "contact",
      cell: (item) => (
        <p className="line-clamp-1">
          {item.row.original.contact?.email || "N/A"}
        </p>
      ),
    },
    {
      header: "Contact Name",
      accessorKey: "contactName",
      cell: (item) => {
        const contact = item.row.original.contact;
        const name = contact 
          ? `${contact.firstname || ""} ${contact.lastname || ""}`.trim()
          : "N/A";
        return <p className="line-clamp-1">{name}</p>;
      },
    },
  ];

  // Prepare table data
  const alertsPanelData = dashboardData?.analytics?.alert_rules || [];
  
  const kpiTableData =
    dashboardData?.analytics?.channel_distribution?.map((ch: any) => {
      const ads = ch.ads_kpi || {};
      const campaign = ch.campaign_kpi || {};
      const summary = ads.campaign_metrics_summary || campaign.campaign_metrics_summary || {};

      return {
        channel: ch.channel || "N/A",
        impressions: ads.impressions || summary.impressions || 0,
        clicks: ads.clicks || summary.clicks || 0,
        conversions: ads.conversions || summary.conversions || 0,
        ctr: ads.ctr || summary.ctr || 0,
        cpc: ads.cpc || summary.cpc || 0,
        cpa: ads.cpa || summary.cpa || 0,
        spend: ads.spend || summary.spend || 0,
        sessions: campaign.sessions || 0,
        influencedContacts: campaign.influencedContacts || 0,
      };
    }) || [];

  const pipelineData = dashboardData?.analytics?.deals || [];

  return (
    <div className="min-h-screen w-full py-3">
     {loading ? (
        <Loading width="50px" height="50px" isLoading={loading} />
      ) : (
        <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">{campaignData?.campaign?.template?.title}</h1>
        <button className="px-2 py-1 rounded-md flex items-center bg-primary-800 text-sm hover:bg-primary-900 text-white" onClick={() => navigate(-1)}>
          <span><MoveLeft strokeWidth={0.75} className="w-2 mr-1 text-white fill-white" /></span><span>Back</span></button>
      </div>

      {/* Dashboard Card */}
      <div className="bg-white rounded-2xl shadow-sm p-3 border-2 border-gray-200">
        {/* Dashboard Header */}
        <div className="flex items-center gap-1 mb-4">
          <BookOpenText  />
          <h2 className="text-lg font-medium">Dashboard</h2>
        </div>

        {/* Campaign Info */}
           <div className="bg-white rounded-lg transition-shadow  cursor-pointer mb-3" >

                {/* Main Info */}
                <div className="mb-2">
        {/* Toggle Button */}
              <button
                // onClick={() => setOpen(!open)}
                className="w-full flex justify-between items-center px-2 py-1 bg-white border border-[#E8EAF2] rounded-lg hover:bg-gray-50 transition"
              >
                <span className="font-medium text-[#24272B] text-sm">Hubspot CRM</span>
                {/* <ChevronDown
                  className={`h-2 w-2 text-[#373D3F] transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                /> */}
              </button>

              {/* Dropdown Content */}
              {/* {open && (
                <div className="mt-[2px] bg-white border rounded-lg shadow-sm p-2 space-y-1">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-sm">{socialData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-sm">{socialData.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">LinkedIn</p>
                    <a
                      href={socialData.linkedin}
                      className="font-medium text-priamry-600 hover:underline text-sm"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {socialData.linkedin}
                    </a>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Company</p>
                    <p className="font-medium">{socialData.company}</p>
                  </div>
                </div>
              )} */}
            </div>
                <div className="space-y-1 text-sm text-gray-700">
                    <div className="flex items-center">
                    <span className="text-blue-600 mr-2">🎯</span>
                    Goal: {campaignData?.campaign?.template?.strategy?.main_goals?.join(", ") || "N/A"}
                    </div>

                    <div className="flex items-center">
                    <span className="text-green-600 mr-2">💰</span>
                    Budget: {campaignData?.campaign?.template?.budgeting?.total_budget || "N/A"}  
                    {/* {campaign.budgeting?.daily_spend_cap &&
                        ` (Daily Cap: ${campaign?.template?.budgeting.total_budget})`} */}
                    </div>

                    <div className="flex items-center">
                    <span className="text-orange-600 mr-2">📅</span>
                    Created: {new Date(campaignData?.campaign?.created_at).toLocaleDateString()}
                    </div>

                    <div className="flex items-center">
                    <span className="text-purple-600 mr-2">📊</span>
                    Attribution: {campaignData?.campaign?.template?.attribution?.models?.join(", ") || "N/A"}
                    </div>

                    <div className="flex items-center">
                    <span className="text-indigo-600 mr-2">🔗</span>
                    Thread ID: {campaignData?.campaign?.thread_id}
                    </div>
                </div>
            </div> 
            <div className="bg-white rounded-2xl shadow-sm p-3 border-2 border-gray-200 mt-4">
                <h2 className="text-lg font-medium mb-3">Suggested Actions</h2>

                {suggestionLoading ? (
                 
                    <LoadingIcon fontSize={52} className="animate-spin text-primary-900 w-3 h-3 flex justify-center items-center w-full" />
                  
                ) : suggestions.length === 0 ? (
                  <p className="text-gray-500 text-center">No suggestions available</p>
                ) : (
                  <div className="flex space-x-4 overflow-x-auto pb-2">
                    {suggestions.map((s, idx) => (
                      <div
                        key={idx}
                        className="min-w-[250px] bg-gray-50 border border-gray-200 rounded-lg p-3 flex-shrink-0"
                      >
                        <p className="font-semibold text-sm mb-1">{s.title}</p>
                        <p className="text-gray-700 text-xs mb-2 line-clamp-3">{s.reason}</p>
                        <p
                          className={`text-xs font-medium ${
                            s.level === "high" ? "text-red-600" :
                            s.level === "medium" ? "text-yellow-600" :
                            "text-green-600"
                          }`}
                        >
                          Severity: {s.level}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
        <div className="mt-4"></div>

        {/* Tabs */}
        <h2 className="text-base font-semibold mb-3">Widgets</h2>
        <div className="flex border-b mb-3 text-sm font-medium border border-[#E7EAF4] rounded-md overflow-hidden">
          {["Budget Donut", "Pipeline", "Follow-up", "Alerts Panel", "Alert History", "KPI Table"].map(tab => (
            <button
              key={tab}
              className={`px-2 text-sm py-1 -mb-px  w-full  ${
                activeTab === tab ? "bg-[#442873] text-white" : " text-[#373D3F]"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
              {tab === "Alerts Panel" && (
                <AlertCircle className="inline w-2 h-2 ml-1 text-red-500" />
              )}
            </button>
          ))}
        </div>

        {/* Chart Section */}
        {activeTab === "Budget Donut" && (
          <div className="flex flex-col items-center w-full">
            <PieChart width={700} height={200}>
              <Pie
                data={chartData}
                cx={150}
                cy={100}
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                    layout="vertical"      
                    verticalAlign="middle" 
                    align="right"         
              />
            </PieChart>

            {/* Data Summary */}
            

          <div className="grid grid-cols-1 w-full mt-2">
                <p className="font-semibold bg-[#F3F3F3] p-1 rounded-t-md text-sm">Data</p>
                <p className="p-1 border-t-2 border-b-[#D9D9D9] text-sm">Planned: ${dashboardData?.analytics?.budget_distribution?.budget_total}</p>
                <p className="p-1 border-t-2 border-b-[#D9D9D9] text-sm">Spent: {dashboardData?.analytics?.budget_distribution?.spend_total}</p>
               <p className="p-1 border-t-2 border-b-[#D9D9D9] text-sm">
                  {dashboardData?.analytics?.budget_distribution?.budget_items?.length > 0 ? (
                    dashboardData.analytics.budget_distribution.budget_items
                      .map((item: any) => `${item.name}: $${item.amount.toLocaleString()}`)
                      .join(",  ")
                  ) : (
                    "No budget items"
                  )}
                </p>

          </div>
        </div>
        )}

         {activeTab === "Alerts Panel" && (
          <div className="mt-4">
            {alertsPanelData.length > 0 ? (
              <ReactTable
                columnsData={alertsPanelColumns}
                rowsData={alertsPanelData}
                size="medium"
                noTopBorder
              />
            ) : (
              <p className="text-gray-500">No alert rules available</p>
            )}
          </div>
        )}

        {activeTab === "Alert History" && (
          <div className="mt-4">
            {alertsArray.length > 0 ? (
              <div className="bg-white border rounded-lg">
                <div className="bg-gray-200 px-4 py-2 rounded-t-lg">
                  <h3 className="font-semibold text-sm">Alert History</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {alertsArray.map((alert:any, index:any) => (
                    <div key={index} className="px-4 py-2 text-sm">
                      {alert}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No alerts</p>
            )}
          </div>
        )}

        {activeTab === "Follow-up" && (
          <div className="mt-4">
            <div className="bg-white border rounded-lg">
              <div className="bg-gray-200 px-4 py-2 rounded-t-lg">
                <h3 className="font-semibold text-sm">Follow-up Summary</h3>
              </div>
              <div className="divide-y divide-gray-200">
                <div className="px-4 py-2 text-sm">
                  <span className="font-medium">No Deal Follow-ups:</span> {dashboardData?.analytics?.no_deal_followups}
                </div>
                <div className="px-4 py-2 text-sm">
                  <span className="font-medium">Deal Follow-ups:</span> {dashboardData?.analytics?.deal_followups}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "KPI Table" && (
          <div className="mt-4">
            {kpiTableData.length > 0 ? (
              <ReactTable
                columnsData={kpiTableColumns}
                rowsData={kpiTableData}
                size="medium"
                noTopBorder
              />
            ) : (
              <p className="text-gray-500">No KPI data available</p>
            )}
          </div>
        )}

          {activeTab === "Pipeline" && (
            <div className="mt-4">
              {pipelineData.length > 0 ? (
                <ReactTable
                  columnsData={pipelineColumns}
                  rowsData={pipelineData}
                  size="medium"
                  noTopBorder
                />
              ) : (
                <p className="text-gray-500">No pipeline data available</p>
              )}
            </div>
          )}

      </div>
      <ContactsWidget />
      <EmailLogs/>
      </>
      )}
    </div>
  );
};

export default CampaignDashboard;